import { useState } from 'react'
import type { ConfigurableField, GrantFieldExposureInput } from '../../api/types'
import { BffError } from '../../bff/errors'
import { useT } from '../../i18n'
import { Button, Modal, TextField } from '../../ui'
import screen from '../screen.module.css'
import styles from './grantModal.module.css'

/**
 * Grants one field, always to exactly one biller: the one selected in the
 * sidebar. The target is FIXED — shown, not offered. There is no biller
 * dropdown and no "Every biller" choice anywhere: a field every biller
 * should see belongs in the default tier of the catalogue (a code change),
 * not in a rule, and the API refuses a grant without a biller for the same
 * reason. This form can only open from a biller context — the "All billers"
 * view is read-only.
 *
 * The service type is a closed list fed from the warehouse, rendered as one
 * radio per type rather than a dropdown — the list is short, and radios put
 * every possible restriction on screen at once. It is not free text: a rule
 * is matched by string equality per row, so a typed value the data has never
 * carried fails CLOSED and silently — the field simply never appears, which
 * reads as a data problem rather than a configuration one.
 *
 * There is no gateway input: that dimension was retired end-to-end
 * (2026-08-24) with the other low-level customisations — a grant applies on
 * every gateway.
 *
 * The reason is required, and the modal enforces it before the request rather
 * than relying on the 400. A grant of somebody's personal data with no recorded
 * justification is the row a compliance review stops on.
 */
export function GrantModal({
  field,
  serviceTypes,
  billerCode,
  billerLabel,
  onClose,
  onSubmit, granted = [] }: {
  readonly field: ConfigurableField
  readonly serviceTypes: readonly string[]
  /** Service types this biller is already granted on the field ('' = any):
   *  not offered again — a grant is unique per (field, biller, service type). */
  readonly granted?: readonly string[]
  /** The sidebar's selected biller — the grant's one and only target. */
  readonly billerCode: string
  /** Its display name, resolved by the caller who already has the registry. */
  readonly billerLabel: string
  readonly onClose: () => void
  readonly onSubmit: (input: GrantFieldExposureInput) => Promise<void>
}) {
  const t = useT()
  const [serviceType, setServiceType] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit() {
    if (!note.trim()) {
      setError(t('fieldExposure.reasonRequired'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await onSubmit({
        field: field.name,
        biller_code: billerCode,
        service_type: serviceType,
        note: note.trim(),
      })
    } catch (err) {
      setError(err instanceof BffError || err instanceof Error ? err.message : t('error.title'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('fieldExposure.grantTitle', { field: field.label })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {t('fieldExposure.grant')}
          </Button>
        </>
      }
    >
      <div className={screen.form}>
        <p className={screen.hint}>{field.help}</p>

        {field.sensitive && (
          <p className={screen.warning} role="alert">
            {t('fieldExposure.sensitiveWarning')}
          </p>
        )}

        <p className={styles.fixedBiller}>
          <strong>{billerLabel}</strong> — {t('fieldExposure.billerFixedHint')}
        </p>

        <fieldset>
          <legend>{t('fieldExposure.serviceType')}</legend>
          <p className={screen.hint}>{t('fieldExposure.serviceTypeHint')}</p>
          <div className={styles.choices}>
            {!granted.includes('') && (
              <label className={styles.choice}>
                <input
                  type="radio"
                  name="grant-service-type"
                  checked={serviceType === ''}
                  onChange={() => setServiceType('')}
                />
                {t('fieldExposure.anyServiceType')}
              </label>
            )}
            {serviceTypes.filter((s) => !granted.includes(s)).map((s) => (
              <label key={s} className={styles.choice}>
                <input
                  type="radio"
                  name="grant-service-type"
                  checked={serviceType === s}
                  onChange={() => setServiceType(s)}
                />
                {s}
              </label>
            ))}
          </div>
        </fieldset>

        <TextField
          label={t('fieldExposure.reason')}
          description={t('fieldExposure.reasonHint')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={512}
          required
        />

        {error && (
          <p className={screen.warning} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
